import React from 'react';
import { Descriptions, Tag, Typography, Space, Button } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { copyToClipboard, getStatusInfo } from '../utils';
import './MetadataPanel.css';

const MetadataPanel = ({ data }) => {
  const statusInfo = getStatusInfo(data.status);
  const isImageJob = !!data.image_job_id;
  
  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toFixed(4);
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return value;
  };
  
  const formatBytes = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };
  
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '-';
    const numSeconds = parseFloat(seconds);
    if (isNaN(numSeconds)) return '-';
    if (numSeconds < 60) return `${numSeconds.toFixed(1)}s`;
    const minutes = Math.floor(numSeconds / 60);
    const remainingSeconds = numSeconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  };
  
  const copyValue = (value) => {
    copyToClipboard(String(value));
  };
  
  return (
    <div className="metadata-panel">
      <div className="metadata-section">
        <h3 className="section-title">🔍 Job Information</h3>
        <Descriptions 
          column={2} 
          bordered 
          size="small"
          className="metadata-table"
        >
          <Descriptions.Item label="Job ID" span={2}>
            <Space>
              <Typography.Text code className="job-id-text">
                {isImageJob ? data.image_job_id : data.workflow_job_id}
              </Typography.Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyValue(isImageJob ? data.image_job_id : data.workflow_job_id)}
              />
            </Space>
          </Descriptions.Item>
          
          <Descriptions.Item label="Status">
            <Tag color={statusInfo.color} icon={statusInfo.icon}>
              {data.status?.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label="Created">
            {new Date(data.created_at).toLocaleString()}
          </Descriptions.Item>
          
          {data.completed_at && (
            <Descriptions.Item label="Completed">
              {new Date(data.completed_at).toLocaleString()}
            </Descriptions.Item>
          )}
          
          <Descriptions.Item label="Duration">
            {formatDuration(data.duration)}
          </Descriptions.Item>
          
          <Descriptions.Item label="Cost">
            <Typography.Text type="success" strong>
              {data.cost_to_client?.toFixed(4) || '0.0000'} URO
            </Typography.Text>
          </Descriptions.Item>
          
          <Descriptions.Item label="Credits Used">
            {data.credits_used || 0}
          </Descriptions.Item>
        </Descriptions>
      </div>
      
      <div className="metadata-section">
        <h3 className="section-title">⚙️ Model Configuration</h3>
        <Descriptions 
          column={2} 
          bordered 
          size="small"
          className="metadata-table"
        >
          <Descriptions.Item label="Model" span={2}>
            <Tag color="blue">
              {data.model_name || data.workflow_definition_name || '-'}
            </Tag>
          </Descriptions.Item>
          
          {data.seed && (
            <Descriptions.Item label="Seed">
              <Typography.Text code>{data.seed}</Typography.Text>
            </Descriptions.Item>
          )}
          
          {data.width && (
            <Descriptions.Item label="Dimensions">
              {data.width} × {data.height}
            </Descriptions.Item>
          )}
          
          {data.steps && (
            <Descriptions.Item label="Steps">
              {data.steps}
            </Descriptions.Item>
          )}
          
          {data.cfg_scale && (
            <Descriptions.Item label="CFG Scale">
              {data.cfg_scale}
            </Descriptions.Item>
          )}
          
          {data.sampler && (
            <Descriptions.Item label="Sampler">
              {data.sampler}
            </Descriptions.Item>
          )}
          
          {data.scheduler && (
            <Descriptions.Item label="Scheduler">
              {data.scheduler}
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>
      
      {data.processing_metrics && (
        <div className="metadata-section">
          <h3 className="section-title">📊 Processing Metrics</h3>
          <Descriptions 
            column={2} 
            bordered 
            size="small"
            className="metadata-table"
          >
            {Object.entries(data.processing_metrics).map(([key, value]) => (
              <Descriptions.Item key={key} label={key.replace(/_/g, ' ').toUpperCase()}>
                {formatValue(value)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      )}
      
      {data.results && Array.isArray(data.results) && data.results.length > 0 && (
        <div className="metadata-section">
          <h3 className="section-title">📁 Output Files</h3>
          <div className="files-list">
            {data.results.map((result, idx) => {
              const fileSize = result.file_size || result.size;
              const fileName = result.file_name || result.name || `output_${idx + 1}`;
              
              return (
                <div key={idx} className="file-item">
                  <div className="file-info">
                    <Typography.Text className="file-name">
                      {fileName}
                    </Typography.Text>
                    <Typography.Text className="file-size">
                      {formatBytes(fileSize)}
                    </Typography.Text>
                  </div>
                  <Space size={4}>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyValue(result.url || result.output_url)}
                    >
                      Copy URL
                    </Button>
                  </Space>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {data.error && (
        <div className="metadata-section error-section">
          <h3 className="section-title">❌ Error Details</h3>
          <div className="error-content">
            <Typography.Text type="danger">
              {data.error.message || data.error}
            </Typography.Text>
            {data.error.details && (
              <Typography.Text className="error-details">
                {JSON.stringify(data.error.details, null, 2)}
              </Typography.Text>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataPanel;
